import React, { useEffect, useRef, createElement } from 'react';
import L from 'leaflet';
const PROPERTY_LAT = -37.8438;
const PROPERTY_LNG = 145.001;
const PROPERTY_LABEL = '500 Toorak Road, Toorak VIC 3142';
const AMENITIES = [{
  name: 'Toorak Village',
  lat: -37.840988,
  lng: 145.008968,
  distance: '500m'
}, {
  name: 'Fawkner Park',
  lat: -37.8383,
  lng: 144.9855,
  distance: '950m'
}, {
  name: 'Melbourne Grammar',
  lat: -37.8339,
  lng: 144.9761,
  distance: '1.2km'
}, {
  name: 'Scotch College',
  lat: -37.8342,
  lng: 145.0294,
  distance: '3.3km'
}, {
  name: 'Kooyong Tennis Club',
  lat: -37.8389,
  lng: 145.032,
  distance: '3.8km'
}];
export function ToorakRoadMap() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    // Inject Leaflet CSS
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    // Wait for CSS to load
    setTimeout(() => {
      if (!mapRef.current) return;
      const map = L.map(mapRef.current, {
        center: [PROPERTY_LAT, PROPERTY_LNG],
        zoom: 14,
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: false
      });
      // CartoDB Positron — clean minimal white tiles
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 19,
        subdomains: 'abcd'
      }).addTo(map);
      // Add zoom control to bottom right
      L.control.zoom({
        position: 'bottomright'
      }).addTo(map);
      // Custom property marker
      const propertyIcon = L.divIcon({
        className: 'property-marker',
        html: `
          <div style="
            width: 14px;
            height: 14px;
            background: #000;
            border-radius: 50%;
            border: 3px solid #fff;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          "></div>
        `,
        iconSize: [14, 14],
        iconAnchor: [7, 7]
      });
      L.marker([PROPERTY_LAT, PROPERTY_LNG], {
        icon: propertyIcon
      }).addTo(map).bindPopup(`<div style="font-family: 'Playfair Display', serif; text-align: center; padding: 4px 0;">
            <strong style="font-size: 13px; letter-spacing: 0.05em;">${PROPERTY_LABEL}</strong>
          </div>`, {
        closeButton: false,
        className: 'property-popup'
      });
      // 15-minute walking radius (~1200m at 80m/min)
      L.circle([PROPERTY_LAT, PROPERTY_LNG], {
        radius: 1200,
        color: '#000',
        weight: 1,
        opacity: 0.25,
        fillColor: '#000',
        fillOpacity: 0.03,
        dashArray: '6, 6',
        interactive: false
      }).addTo(map);
      // Walking radius label
      const walkLabelIcon = L.divIcon({
        className: 'walk-label',
        html: `<span style="
          font-family: 'Montserrat', sans-serif;
          font-size: 9px;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: rgba(0,0,0,0.3);
          white-space: nowrap;
          background: rgba(255,255,255,0.8);
          padding: 2px 6px;
        ">15 min walk</span>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0]
      });
      // Place label at the top of the circle (1200m north)
      const labelLat = PROPERTY_LAT + 1200 / 111320;
      L.marker([labelLat, PROPERTY_LNG], {
        icon: walkLabelIcon,
        interactive: false
      }).addTo(map);
      // Amenity markers
      const amenityIcon = L.divIcon({
        className: 'amenity-marker',
        html: `
          <div style="
            width: 12px;
            height: 12px;
            background: #000;
            border-radius: 50%;
            border: 2px solid #fff;
            box-shadow: 0 1px 4px rgba(0,0,0,0.25);
          "></div>
        `,
        iconSize: [12, 12],
        iconAnchor: [6, 6]
      });
      AMENITIES.forEach(poi => {
        L.marker([poi.lat, poi.lng], {
          icon: amenityIcon
        }).addTo(map).bindPopup(`<div style="font-family: 'Montserrat', sans-serif; text-align: center; padding: 2px 0;">
              <span style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: #737373;">${poi.name}</span>
              <br/>
              <span style="font-size: 10px; color: #999;">${poi.distance}</span>
            </div>`, {
          closeButton: false
        });
      });
      // Attribution in bottom-left, subtle
      L.control.attribution({
        position: 'bottomleft',
        prefix: false
      }).addAttribution('© <a href="https://www.openstreetmap.org/copyright" style="color:#aaa;">OpenStreetMap</a> · <a href="https://carto.com/" style="color:#aaa;">CARTO</a>').addTo(map);
      mapInstanceRef.current = map;
      // Handle resize
      const handleResize = () => map.invalidateSize();
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, 100);
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);
  return <section id="location" className="relative bg-white border-y border-black/5" data-id="element-97">
      {/* Section header */}
      <div className="px-6 md:px-12 pt-16 md:pt-24 pb-10 md:pb-16" data-id="element-98">
        <div className="max-w-7xl mx-auto" data-id="element-99">
          <div className="flex items-center gap-4 mb-4 md:mb-6" data-id="element-100">
            <div className="w-8 h-[1px] bg-black" data-id="element-101"></div>
            <span className="font-body text-[10px] text-black tracking-widest uppercase" data-id="element-102">
              The Location
            </span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6" data-id="element-103">
            <h2 className="font-display text-4xl md:text-5xl text-black leading-[1.1]" data-id="element-104">
              Toorak's Most <br data-id="element-105" />
              <span className="italic text-gray-muted" data-id="element-106">Coveted Address.</span>
            </h2>
            <div className="flex flex-wrap gap-2" data-id="element-107">
              {AMENITIES.map(poi => <span key={poi.name} className="font-body text-[10px] tracking-widest uppercase text-gray-muted border border-black/10 px-3 py-1.5 whitespace-nowrap" data-id="element-108">
                  {poi.name} · {poi.distance}
                </span>)}
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div ref={mapRef} className="w-full" style={{
      height: '65vh',
      minHeight: '500px'
    }} data-id="element-109" />

      {/* Leaflet overrides */}
      <style dangerouslySetInnerHTML={{
      __html: `
        .leaflet-container {
          font-family: 'Montserrat', sans-serif;
        }
        .leaflet-control-zoom {
          border: 1px solid rgba(0,0,0,0.1) !important;
          border-radius: 0 !important;
          box-shadow: none !important;
        }
        .leaflet-control-zoom a {
          color: #000 !important;
          border-radius: 0 !important;
          border-bottom: 1px solid rgba(0,0,0,0.1) !important;
          width: 36px !important;
          height: 36px !important;
          line-height: 36px !important;
          font-size: 16px !important;
        }
        .leaflet-control-zoom a:last-child {
          border-bottom: none !important;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 0 !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.12) !important;
          padding: 8px 12px !important;
        }
        .leaflet-popup-tip {
          box-shadow: none !important;
        }
        .leaflet-control-attribution {
          font-size: 9px !important;
          background: transparent !important;
          color: #ccc !important;
        }
        .leaflet-control-attribution a {
          color: #aaa !important;
        }
      `
    }} data-id="element-110" />
    </section>;
}